import { useLazyQuery, useMutation } from '@apollo/client';
import FormContainer from '@components/FormContainer';
import Text from '@components/Text';
import { GET_OPTIONS_TALENT } from '@gql/options';
import { GET_TALENT, UPDATE_SOFT_SKILLS_TALENT } from '@gql/talents';
import { Stack, useMediaQuery } from '@mui/material';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AutoCompleteSoftSkills } from '@components/AutoComplete/AutoCompleteSoftSkills';

type Props = {
    talent?: any;
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
    onBack?: () => void;
    isEdit?: boolean;
};

function TalentSoftSkillsForm(props: Props) {
    const [options, setOptions] = useState({
        softSkills: [],
    });

    const { showSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [fetch] = useLazyQuery(GET_OPTIONS_TALENT, {
        onCompleted: result => {
            setOptions(result?.options);
        },
    });
    const talent = props.talent;

    const defaultValues = {
        softSkills: talent?.softSkills ?? [],
        softSkillIds: talent?.softSkillIds ?? [],
    };

    const {
        control,
        handleSubmit,
        getValues,
        reset,
        formState: { isValid },
    } = useForm({
        mode: 'all',
        defaultValues,
    });

    const [update] = useMutation(UPDATE_SOFT_SKILLS_TALENT, {
        onCompleted: () => {
            showSnackbar('Savoir-être mis à jour avec succès', 'success');
            props?.onSuccess?.(getValues());
            setLoading(false);
            setFormState('view');
        },
        onError: error => {
            props?.onError?.(error);
            setLoading(false);
        },
    });

    const submitHandler = useCallback(
        data => {
            if (!isValid) return;
            setLoading(true);

            update({
                variables: {
                    payload: data,
                    talentId: talent?._id,
                },
                refetchQueries: () => [
                    {
                        query: GET_OPTIONS_TALENT,
                    },
                    {
                        query: GET_TALENT,
                        variables: { talentId: talent?._id }
                    }
                ],
            });
        },
        [isValid, talent?._id, update],
    );

    useEffect(() => {
        fetch();
    }, [fetch]);

    const [formState, setFormState] = useState<'view' | 'edit'>('view');

    return (
        <FormContainer
            onBack={props.onBack}
            loading={loading}
            formState={formState}
            setFormState={setFormState}
            onCancel={() => {
                reset(defaultValues);
            }}
            onSubmit={handleSubmit(submitHandler)}
            formTitle="Savoir-être">
            <AutoCompleteSoftSkills
                allowAdd
                viewOnly={formState === 'view'}
                placeholder="Sélectionnez les savoir-être"
                control={control}
                name="softSkillIds"
                groupedOptions={options?.softSkills}
            />
        </FormContainer>
    );
}

export const SoftSkillsEmpty = () => {
    const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('md'));

    return (
        <Stack p={3} gap={3} justifyContent={'center'} alignItems={'center'} zIndex={-1}>
            <img src="/images/soft-skills.svg" alt="Savoir-être" width={isMobile ? 150 : 200} />
            <Stack maxWidth={435} gap={1}>
                <Text textAlign="center" heading dark bold size={20}>
                    Dites-nous qui vous êtes
                </Text>
                <Text textAlign="center" darkGray size={isMobile ? 12 : 16}>
                    Rendez votre profil plus visible en sélectionnant les savoir-être qui vous correspondent le mieux.
                </Text>
            </Stack>
        </Stack>
    );
};

export default TalentSoftSkillsForm;
